import { Component, type ErrorInfo, type ReactNode } from 'react';
import './site.css';

const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|ChunkLoadError/i;
const RETRY_KEY = 'subfocus:chunk-retry';
const RETRY_WINDOW_MS = 30_000;

/** Reloads at most once per window, so a real outage cannot become a reload loop. */
function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RETRY_KEY) ?? 0);
    if (Date.now() - last < RETRY_WINDOW_MS) return false;
    sessionStorage.setItem(RETRY_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

interface State {
  failed: boolean;
  retrying: boolean;
}

/**
 * Pages load on demand. After a deploy, an open tab asks for files that no longer exist,
 * which a fresh load fixes. Anything else gets a way forward instead of a blank page.
 */
export class AppBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false, retrying: false };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppBoundary]', error, info.componentStack);
    if (CHUNK_ERROR.test(error.message) && reloadOnce()) this.setState({ retrying: true });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    if (this.state.retrying) return null;
    return (
      <div className="sfp">
        <main className="sfp-main">
          <span className="sfp-eyebrow">subfocus.ai</span>
          <h1 className="sfp-title">Something didn't load.</h1>
          <p className="sfp-lede">It's usually your connection or a site update. A refresh normally sorts it out.</p>
          <button type="button" className="sfp-button" onClick={() => window.location.reload()}>Refresh the page</button>
          <p className="sfp-note">Still stuck? Email <a className="sfp-inline" href="mailto:hello@subfocus.ai">hello@subfocus.ai</a>.</p>
        </main>
      </div>
    );
  }
}
